import { Alert, Autocomplete, Button, Group, Modal, SegmentedControl, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { DataBankEntryDto, DataBankSection, FileRefDto } from '@mexp/shared';
import { http } from '@/api/client';
import { FileUploader } from '@/features/files/FileUploader';
import { CountrySelect } from '@/features/requests/components/RequestFields';
import { useAction } from '@/lib/useAction';

export interface DataBankContext {
  party: string;
  country: string | null;
  product: string;
}

/** Upload files (or add a link) to one Data Bank section, with the party/country/product that section needs. */
export function DataBankUploadModal({
  section,
  opened,
  onClose,
  initial,
}: {
  section: DataBankSection;
  opened: boolean;
  onClose: () => void;
  initial: DataBankContext;
}) {
  const [mode, setMode] = useState<'files' | 'link'>('files');
  const [ctx, setCtx] = useState<DataBankContext>(initial);
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [link, setLink] = useState({ url: '', name: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const parties = useQuery({ queryKey: ['data-bank', 'parties'], queryFn: () => http.get<{ items: string[] }>('/data-bank/parties'), enabled: opened && section.needsParty });
  const products = useQuery({ queryKey: ['data-bank', 'products'], queryFn: () => http.get<{ items: string[] }>('/data-bank/products'), enabled: opened && section.needsProduct });

  useEffect(() => {
    if (opened) {
      setCtx(initial);
      setFiles([]);
      setLink({ url: '', name: '', notes: '' });
      setMode('files');
    }
  }, [opened, initial]);

  const missing = [
    section.needsParty && !ctx.party.trim() ? 'party' : null,
    section.needsCountry && !ctx.country ? 'country' : null,
    section.needsProduct && !ctx.product.trim() ? 'product' : null,
  ].filter(Boolean) as string[];
  const context = {
    section: section.key,
    party: section.needsParty ? ctx.party.trim() : undefined,
    country: section.needsCountry ? (ctx.country ?? undefined) : undefined,
    product: section.needsProduct ? ctx.product.trim() : undefined,
  };

  const save = useAction(
    (): Promise<unknown> =>
      mode === 'files'
        ? http.post<{ items: DataBankEntryDto[] }>('/data-bank/entries', { ...context, fileIds: files.map((f) => f.id) })
        : http.post<DataBankEntryDto>('/data-bank/links', { ...context, url: link.url.trim(), name: link.name.trim() || undefined, notes: link.notes.trim() || undefined }),
    {
      success: mode === 'files' ? `${files.length} file${files.length === 1 ? '' : 's'} added to ${section.label}` : 'Link added',
      invalidate: [['data-bank']],
      onDone: onClose,
    },
  );
  const linkInvalid = mode === 'link' && !/^https?:\/\/\S+$/i.test(link.url.trim());

  return (
    <Modal opened={opened} onClose={onClose} title={`Add to ${section.label}`} size="lg">
      <Stack>
        {section.needsParty && (
          <Autocomplete
            label="Party (exporter)"
            placeholder="Choose or type a party"
            data={parties.data?.items ?? []}
            value={ctx.party}
            onChange={(party) => setCtx((c) => ({ ...c, party }))}
            withAsterisk
            limit={30}
          />
        )}
        {section.needsCountry && <CountrySelect value={ctx.country} onChange={(country) => setCtx((c) => ({ ...c, country }))} />}
        {section.needsProduct && (
          <Autocomplete
            label="Product"
            placeholder="e.g. Paracetamol 500 mg Tablet"
            data={products.data?.items ?? []}
            value={ctx.product}
            onChange={(product) => setCtx((c) => ({ ...c, product }))}
            withAsterisk
            limit={30}
          />
        )}
        <SegmentedControl
          value={mode}
          onChange={(v) => setMode(v as 'files' | 'link')}
          data={[
            { label: 'Upload files', value: 'files' },
            { label: 'Add a link', value: 'link' },
          ]}
        />
        {mode === 'files' ? (
          missing.length ? (
            <Alert variant="light" color="gray">
              Fill in the {missing.join(', ')} first; files are filed under them.
            </Alert>
          ) : (
            <FileUploader area="data_bank" context={context as Record<string, string>} value={files} onChange={setFiles} onBusyChange={setBusy} hint={`Usually ${section.accept.join(', ').toUpperCase()}`} />
          )
        ) : (
          <Stack gap="sm">
            <TextInput label="Link" placeholder="https://…" value={link.url} onChange={(e) => setLink((l) => ({ ...l, url: e.currentTarget.value }))} withAsterisk />
            <TextInput label="Name" placeholder="Shown in the list" value={link.name} onChange={(e) => setLink((l) => ({ ...l, name: e.currentTarget.value }))} />
            <Textarea label="Notes" autosize minRows={2} value={link.notes} onChange={(e) => setLink((l) => ({ ...l, notes: e.currentTarget.value }))} />
          </Stack>
        )}
        <Text fz="xs" c="dimmed">
          {section.description}
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={busy || missing.length > 0 || (mode === 'files' ? !files.length : linkInvalid)}
            onClick={() => save.mutate()}
          >
            {mode === 'files' ? 'Save to Data Bank' : 'Add link'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
